// src/pages/ClaimVerification.js
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { toast } from 'sonner'
import { ArrowLeft, Lock, AlertTriangle, CheckCircle, Send, XCircle } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const socialLinkFields = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourprofile' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile' },
  { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/yourprofile' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/yourprofile' },
  { key: 'reddit', label: 'Reddit', placeholder: 'https://reddit.com/u/yourprofile' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourprofile' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourprofile' },
  { key: 'website', label: 'Website', placeholder: 'https://yourwebsite.com' },
]

export default function ClaimVerification() {
  const navigate = useNavigate()
  const { token } = useAuth()
  const { isDarkMode } = useTheme()
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [claimData, setClaimData] = useState(null)
  const [formData, setFormData] = useState({})
  const [contactEmail, setContactEmail] = useState('')

  useEffect(() => {
    fetchClaimStatus()
  }, [])

  const fetchClaimStatus = async () => {
    try {
      const response = await axios.get(`${API}/profiles/claim/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (response.data.has_claim) {
        setClaimData(response.data)
        // Initialize form with empty values for vacant links
        const initialForm = {}
        socialLinkFields.forEach(f => {
          if (!response.data.social_links?.[f.key]) {
            initialForm[f.key] = ''
          }
        })
        setFormData(initialForm)
      } else {
        // No claim found, redirect to create profile
        toast.error('No active profile claim found')
        navigate('/create-profile')
      }
    } catch (err) {
      toast.error('Failed to load claim status')
      navigate('/create-profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!contactEmail.trim()) {
      toast.error('Please provide a contact email for verification')
      return
    }

    setIsSubmitting(true)
    try {
      const social_links = {}
      Object.entries(formData).forEach(([key, value]) => {
        if (value?.trim()) {
          social_links[key] = value.trim()
        }
      })

      await axios.post(
        `${API}/profiles/claim/verify`,
        {
          social_links: Object.keys(social_links).length > 0 ? social_links : null,
          contact_email: contactEmail.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      toast.success('Verification submitted! Our team will review your claim.')
      navigate('/home')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit verification')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this claim? The profile will become available for others to claim.')) {
      return
    }

    setIsCancelling(true)
    try {
      await axios.post(`${API}/profiles/claim/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      toast.success('Claim cancelled')
      navigate('/create-profile')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel claim')
    } finally {
      setIsCancelling(false)
    }
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!claimData) {
    return null
  }

  const existingLinks = claimData.social_links || {}
  const confirmedLinks = claimData.confirmed_links || []

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className={isDarkMode ? 'text-gray-300 hover:text-white' : ''}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <DarkModeToggle />
        </div>

        {/* Main Card */}
        <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90'} backdrop-blur-sm`}>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${isDarkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                <AlertTriangle className={`w-8 h-8 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              </div>
            </div>
            <CardTitle className={`text-2xl ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Claim Profile: @{claimData.username}
            </CardTitle>
            <CardDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Complete verification to claim this profile. Links provided by the scout are locked.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-indigo-900/30 border-indigo-700' : 'bg-indigo-50 border-indigo-200'} border`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                    Pending Verification
                  </Badge>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  Your profile is in read-only mode until verification is complete. You can only add links to vacant slots.
                </p>
              </div>

              {/* Verification Instructions */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'} border`}>
                <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                  📋 Verification Instructions:
                </h3>
                <p className={`text-sm mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                  Add your Public Respect profile link to your below shown social media public-viewable bios or website:
                </p>
                <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-green-300'}`}>
                  <code className={`text-sm break-all ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                    {window.location.origin}/profile/{claimData.username}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/profile/${claimData.username}`)
                      toast.success('Profile link copied!')
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <p className={`text-sm mt-3 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                  Our review team will check if this link is present in your social media profiles or website.
                </p>
              </div>

              {/* Profile Info (Read Only) */}
              <div className="space-y-4">
                <div>
                  <Label className={isDarkMode ? 'text-gray-200' : ''}>Name</Label>
                  <Input
                    value={claimData.name}
                    disabled
                    className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-100 text-gray-600'}`}
                  />
                </div>
              </div>

              {/* Social Links */}
              <div>
                <Label className={`mb-3 block ${isDarkMode ? 'text-gray-200' : ''}`}>
                  Social Links
                </Label>
                
                <div className="grid grid-cols-1 gap-3">
                  {socialLinkFields.map((field) => {
                    const isLocked = !!existingLinks[field.key]
                    const isConfirmed = confirmedLinks.includes(field.key)
                    
                    return (
                      <div key={field.key}>
                        <Label htmlFor={field.key} className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {field.label}
                          {isLocked && <Lock className="w-3 h-3 text-orange-500" />}
                          {isConfirmed && <CheckCircle className="w-3 h-3 text-green-500" />}
                        </Label>
                        <Input
                          id={field.key}
                          data-testid={`claim-${field.key}-input`}
                          type="url"
                          value={isLocked ? existingLinks[field.key] : (formData[field.key] || '')}
                          onChange={(e) => !isLocked && setFormData({ ...formData, [field.key]: e.target.value })}
                          disabled={isLocked}
                          className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''} ${isLocked ? 'opacity-75 cursor-not-allowed' : ''}`}
                          placeholder={isLocked ? '' : field.placeholder}
                        />
                        {isLocked && (
                          <p className="text-xs text-orange-500 mt-1">
                            Locked by profile scout - cannot be edited
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Contact Email */}
              <div>
                <Label htmlFor="contact_email" className={isDarkMode ? 'text-gray-200' : ''}>
                  Contact Email * (for verification team)
                </Label>
                <Input
                  id="contact_email"
                  data-testid="claim-contact-email-input"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                  placeholder="your@email.com"
                  required
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Our team may contact you to verify your identity
                </p>
              </div>

              {/* Note */}
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-orange-900/30 border-orange-700' : 'bg-orange-50 border-orange-200'} border text-sm`}>
                <p className={isDarkMode ? 'text-orange-300' : 'text-orange-700'}>
                  <strong>Note:</strong> If verification fails, you will be redirected to create a new profile. The unclaimed profile will remain available for the rightful owner.
                </p>
              </div>

              {/* Actions */}
              <div className="sticky bottom-0 pt-4 pb-2 bg-inherit -mx-6 px-6 sm:relative sm:mx-0 sm:px-0 sm:pb-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    data-testid="claim-cancel-button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isCancelling || isSubmitting}
                    className="flex-1 order-2 sm:order-1"
                  >
                    {isCancelling ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Claim
                      </>
                    )}
                  </Button>
                  
                  <Button
                    type="submit"
                    data-testid="claim-submit-button"
                    disabled={isSubmitting || isCancelling || !contactEmail.trim()}
                    className="flex-1 order-1 sm:order-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-3"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit for Review
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
